// ===============================================
// 🎫 TICKETSYSTEM.TSX - VERSIÓN CORREGIDA UX/UI
// ===============================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Ticket,
  AlertCircle,
  MessageCircle,
  Wrench,
  Settings,
  User,
  Clock,
  Timer,
  Paperclip,
  Tag,
  Plus,
  RefreshCw,
  X,
  Send,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Search,
  CheckCircle,
  Circle,
  PlayCircle,
  PauseCircle,
  XCircle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { ticketService } from '../../services/ticketService';
import CreateTicketForm from '../CreateTicketForm';

// ===============================================
// 🔧 INTERFACES
// ===============================================

interface Ticket {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  motor: string;
  configuration: string;
  component: string;
  user_name: string;
  user_email: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  estimated_hours?: number;
  rating?: number;
  comments?: any[];
  attachments?: any[];
  comment_count?: number;
  attachment_count?: number;
}

interface DashboardStats {
  totalTickets: number;
  ticketsAbiertos: number;
  ticketsEnProgreso: number;
  ticketsResueltos: number;
  ticketsCerrados: number;
  promedioSatisfaccion: number;
}

// ===============================================
// 🎫 COMPONENTE PRINCIPAL
// ===============================================

const TicketSystem: React.FC = () => {
  const { isDark } = useTheme();
  const { user: currentUser } = useAuth();

  // ===============================================
  // 🎯 ESTADOS
  // ===============================================
  
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState<File[]>([]);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [notification, setNotification] = useState<{message: string; type: 'success' | 'error' | 'info'} | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Estados dashboard
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalTickets: 0,
    ticketsAbiertos: 0,
    ticketsEnProgreso: 0,
    ticketsResueltos: 0,
    ticketsCerrados: 0,
    promedioSatisfaccion: 0
  });

  // Estados de filtros
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // ===============================================
  // 🎨 ESTILOS MEJORADOS
  // ===============================================

  const cardClasses = `rounded-lg shadow-sm border transition-all duration-200 ${
    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-900';
  const subTextClasses = isDark ? 'text-gray-300' : 'text-gray-600';
  const bgClasses = isDark ? 'bg-gray-700' : 'bg-gray-50';
  
  const primaryButtonClasses = `px-4 py-2 bg-blue-600 text-white rounded-md font-medium text-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`;
  
  const secondaryButtonClasses = `px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 text-sm`;

  // ===============================================
  // 🔄 FUNCIONES PRINCIPALES
  // ===============================================

  const updateGlobalHeaderStats = useCallback(() => {
  // Evento personalizado para actualizar estadísticas globales
  const headerUpdateEvent = new CustomEvent('updateHeaderStats', {
    detail: {
      total: tickets.length,
      abiertos: tickets.filter(t => t.status === 'open').length,
      resueltos: tickets.filter(t => t.status === 'resolved').length,
      satisfaccion: tickets.filter(t => t.rating).reduce((acc, t) => acc + (t.rating || 0), 0) / tickets.filter(t => t.rating).length || 0
    }
  });
  
  window.dispatchEvent(headerUpdateEvent);
}, [tickets]);

// Llamar la función cuando cambien los tickets
useEffect(() => {
  updateGlobalHeaderStats();
}, [updateGlobalHeaderStats]);

  const loadDashboardStats = useCallback(async () => {
    try {
      const stats = await ticketService.getDashboardStats();
      
      const updatedStats: DashboardStats = {
        totalTickets: stats.by_status.reduce((sum: any, item: any) => sum + item.count, 0),
        ticketsAbiertos: stats.by_status.find((s: any) => s.status === 'open')?.count || 0,
        ticketsEnProgreso: stats.by_status.find((s: any) => s.status === 'in_progress')?.count || 0,
        ticketsResueltos: stats.by_status.find((s: any) => s.status === 'resolved')?.count || 0,
        ticketsCerrados: stats.by_status.find((s: any) => s.status === 'closed')?.count || 0,
        promedioSatisfaccion: Math.round((stats.averages.avg_rating || 0) * 10) / 10
      };
      
      setDashboardStats(updatedStats);
      
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      // Fallback con datos de tickets actuales
      setDashboardStats({
        totalTickets: tickets.length,
        ticketsAbiertos: tickets.filter(t => t.status === 'open').length,
        ticketsEnProgreso: tickets.filter(t => t.status === 'in_progress').length,
        ticketsResueltos: tickets.filter(t => t.status === 'resolved').length,
        ticketsCerrados: tickets.filter(t => t.status === 'closed').length,
        promedioSatisfaccion: 0
      });
    }
  }, [tickets]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await ticketService.getTickets();
      setTickets(response.tickets || []);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Error cargando tickets';
      setError(errorMessage);
      setTickets([]);
      showNotification('Error cargando tickets', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  
useEffect(() => {
  const updateHeaderStats = () => {
    if (tickets.length > 0) {
      // Calcular estadísticas para el header superior
      const abiertos = tickets.filter(t => t.status === 'open').length;
      const resueltos = tickets.filter(t => t.status === 'resolved').length;
      const enProgreso = tickets.filter(t => t.status === 'in_progress').length;
      const promedio = tickets.filter(t => t.rating).reduce((acc, t) => acc + (t.rating || 0), 0) / tickets.filter(t => t.rating).length || 0;
      
      // ✅ ACTUALIZAR HEADER SUPERIOR (si existe elemento en el DOM)
      const headerStats = document.querySelectorAll('.header-stat-value');
      if (headerStats.length >= 4) {
        headerStats[0].textContent = tickets.length.toString(); // Total
        headerStats[1].textContent = resueltos.toString(); // Resueltos Hoy
        headerStats[2].textContent = promedio.toFixed(1); // Satisfacción
        headerStats[3].textContent = abiertos.toString(); // Chats Activos
      }
    }
  };

  updateHeaderStats();
}, [tickets]);

  
  const handleCreateTicket = () => {
    setShowCreateModal(true);
  };

  const handleTicketCreated = () => {
    loadTickets();
    setShowCreateModal(false);
    showNotification('Ticket creado exitosamente', 'success');
  };

  const loadTicketDetail = async (ticketId: string) => {
    setLoadingTicket(true);
    try {
      const ticket = await ticketService.getTicket(ticketId);
      setSelectedTicket(ticket);
    } catch (err) {
      console.error('Error loading ticket detail:', err);
      showNotification('Error cargando detalles del ticket', 'error');
    } finally {
      setLoadingTicket(false);
    }
  };

  // ✅ FUNCIÓN CORREGIDA para cambio de estado
  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      await ticketService.updateTicket(ticketId, { status: newStatus });
      
      // Actualizar el ticket en la lista
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, status: newStatus as any }
            : ticket
        )
      );
      
      // Actualizar el ticket seleccionado si es el mismo
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => prev ? { ...prev, status: newStatus as any } : null);
      }
      
      showNotification('Estado actualizado correctamente', 'success');
    } catch (err: any) {
      console.error('Error actualizando estado:', err);
      showNotification('Error al actualizar estado: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ✅ FUNCIÓN CORREGIDA para agregar comentarios
  const handleAddComment = async () => {
    if (!selectedTicket || !commentText.trim()) {
      showNotification('Debe escribir un comentario', 'error');
      return;
    }

    try {
    // ✅ CORRECCIÓN: Crear objeto de comentario válido
    const commentData = {
      content: commentText,
      author: currentUser?.name || 'Usuario',
      author_role: currentUser?.role || 'user'
    };

    // ✅ CORRECCIÓN: Enviar JSON válido, no string con comillas
    await ticketService.addComment(selectedTicket.id, commentData, commentAttachments);
    
    setCommentText('');
    setCommentAttachments([]);
    setShowCommentForm(false);
    await loadTicketDetail(selectedTicket.id);
    showNotification('Comentario agregado exitosamente', 'success');
  } catch (err: any) {
    console.error('Error agregando comentario:', err);
    showNotification('Error agregando comentario: ' + (err.message || 'Error desconocido'), 'error');
  }
};

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  // ===============================================
  // 🛠️ FUNCIONES AUXILIARES
  // ===============================================

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Circle className="w-4 h-4" />;
      case 'in_progress': return <PlayCircle className="w-4 h-4" />;
      case 'waiting_user': return <PauseCircle className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'open': return 'Abierto';
      case 'in_progress': return 'En Progreso';
      case 'waiting_user': return 'Esperando Usuario';
      case 'resolved': return 'Resuelto';
      case 'closed': return 'Cerrado';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-green-600 bg-green-100 border-green-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'waiting_user': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'resolved': return 'text-purple-600 bg-purple-100 border-purple-200';
      case 'closed': return 'text-gray-600 bg-gray-100 border-gray-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-700 bg-red-100 border-red-200';
      case 'high': return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'medium': return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'low': return 'text-green-700 bg-green-100 border-green-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.motor.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // ===============================================
  // 🎨 COMPONENTES DE RENDERIZADO
  // ===============================================

  const renderNotification = () => {
    if (!notification) return null;

    const bgColor = notification.type === 'success' ? 'bg-green-500' : 
                   notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500';

    return (
      <div className={`fixed top-4 right-4 ${bgColor} text-white px-4 py-3 rounded-md shadow-lg z-50 max-w-md`}>
        <div className="flex items-center justify-between">
          <span className="text-sm">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="ml-3">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <div className={`${cardClasses} p-4 mb-6 border-red-200 bg-red-50`}>
        <div className="flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <h3 className="font-medium text-red-800">Error</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ✅ LISTA DE TICKETS MEJORADA
  const renderTicketsList = () => {
    if (loading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`${cardClasses} p-4 animate-pulse`}>
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-2"></div>
              <div className="flex space-x-2">
                <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (filteredTickets.length === 0) {
      return (
        <div className={`${cardClasses} p-8 text-center`}>
          <Ticket className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className={`text-lg font-medium ${textClasses} mb-2`}>
            No hay tickets
          </h3>
          <p className={`${subTextClasses} text-sm`}>
            {searchTerm ? 'No se encontraron tickets con esos criterios.' : 'Crea tu primer ticket.'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredTickets.map((ticket) => (
          <div
            key={ticket.id}
            onClick={() => loadTicketDetail(ticket.id)}
            className={`${cardClasses} p-4 cursor-pointer hover:shadow-md transition-all duration-200 ${
              selectedTicket?.id === ticket.id 
                ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {/* Header del ticket */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium ${textClasses} text-sm mb-1 truncate`}>
                  {ticket.title}
                </h4>
                <p className={`${subTextClasses} text-xs mb-2 line-clamp-2`}>
                  {ticket.description}
                </p>
              </div>
              
              {/* Prioridad */}
              <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(ticket.priority)} ml-2 shrink-0`}>
                {ticket.priority.toUpperCase()}
              </span>
            </div>

            {/* Info del motor */}
            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
              <div className={`flex items-center ${subTextClasses}`}>
                <Wrench className="w-3 h-3 mr-1" />
                <span className="truncate">{ticket.motor}</span>
              </div>
              <div className={`flex items-center ${subTextClasses}`}>
                <Settings className="w-3 h-3 mr-1" />
                <span className="truncate">{ticket.configuration}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Estado */}
                <div className={`flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                  {getStatusIcon(ticket.status)}
                  <span className="ml-1">{getStatusText(ticket.status)}</span>
                </div>
                
                {/* Comentarios */}
                {(ticket.comment_count || 0) > 0 && (
                  <div className={`flex items-center ${subTextClasses} text-xs`}>
                    <MessageCircle className="w-3 h-3 mr-1" />
                    <span>{ticket.comment_count}</span>
                  </div>
                )}
              </div>
              
              {/* Fecha */}
              <span className={`${subTextClasses} text-xs`}>
                {new Date(ticket.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ✅ DETALLE DE TICKET MEJORADO
  const renderTicketDetail = () => {
    if (!selectedTicket) {
      return (
        <div className={`${cardClasses} p-8 text-center`}>
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className={`text-xl font-medium ${textClasses} mb-2`}>
            Selecciona un Ticket
          </h3>
          <p className={`${subTextClasses}`}>
            Elige un ticket de la lista para ver los detalles
          </p>
        </div>
      );
    }

    if (loadingTicket) {
      return (
        <div className={`${cardClasses} p-8 text-center`}>
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className={`${subTextClasses}`}>Cargando detalles...</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Header del ticket */}
        <div className={`${cardClasses} p-6`}>
          {/* Controles en la parte superior */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center space-x-3">
              {/* Estado */}
              <select
                value={selectedTicket.status}
                onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                disabled={updatingStatus}
                className={`px-3 py-1 rounded border text-sm font-medium ${getStatusColor(selectedTicket.status)} ${
                  updatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <option value="open">🟢 Abierto</option>
                <option value="in_progress">🔵 En Progreso</option>
                <option value="waiting_user">🟡 Esperando Usuario</option>
                <option value="resolved">🟣 Resuelto</option>
                <option value="closed">⚫ Cerrado</option>
              </select>

              {/* Prioridad */}
              <span className={`px-3 py-1 rounded text-sm font-medium border ${getPriorityColor(selectedTicket.priority)}`}>
                {selectedTicket.priority.toUpperCase()}
              </span>

              {/* Contadores */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <span className={`flex items-center px-2 py-1 ${bgClasses} rounded text-sm`}>
                  <Paperclip className="w-4 h-4 mr-1" />
                  {selectedTicket.attachments.length}
                </span>
              )}

              <span className={`flex items-center px-2 py-1 ${bgClasses} rounded text-sm`}>
                <MessageCircle className="w-4 h-4 mr-1" />
                {(selectedTicket.comments?.length || 0)}
              </span>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={() => setSelectedTicket(null)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Título y información principal */}
          <div className="mb-4">
            <h2 className={`text-xl font-bold ${textClasses} mb-2`}>
              {selectedTicket.title}
            </h2>
            <p className={`text-sm ${subTextClasses} mb-4`}>
              Ticket #{selectedTicket.id}
            </p>
            
            {/* Información del motor */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className={`text-xs ${subTextClasses} uppercase tracking-wider`}>Motor</label>
                <p className={`font-medium ${textClasses}`}>{selectedTicket.motor}</p>
              </div>
              <div>
                <label className={`text-xs ${subTextClasses} uppercase tracking-wider`}>Configuración</label>
                <p className={`font-medium ${textClasses}`}>{selectedTicket.configuration}</p>
              </div>
              <div>
                <label className={`text-xs ${subTextClasses} uppercase tracking-wider`}>Componente</label>
                <p className={`font-medium ${textClasses}`}>{selectedTicket.component}</p>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div className="mb-4">
            <label className={`text-sm font-medium ${textClasses} mb-2 block`}>
              Descripción del Problema
            </label>
            <div className={`p-3 ${bgClasses} rounded text-sm`}>
              {selectedTicket.description}
            </div>
          </div>

          {/* Información del usuario */}
          <div className="flex items-center space-x-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-blue-500" />
              <div>
                <p className={`text-xs ${subTextClasses}`}>Reportado por</p>
                <p className={`text-sm font-medium ${textClasses}`}>
                  {selectedTicket.user_name} ({selectedTicket.user_email})
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-500" />
              <div>
                <p className={`text-xs ${subTextClasses}`}>Creado</p>
                <p className={`text-sm ${textClasses}`}>
                  {new Date(selectedTicket.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comentarios */}
        <div className={`${cardClasses} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-medium ${textClasses}`}>
              Comentarios ({selectedTicket.comments?.length || 0})
            </h3>
           <button
            onClick={() => setShowCommentForm(true)}
            className="group relative px-4 py-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-lg font-medium text-sm shadow-md hover:shadow-indigo-500/25 transition-all duration-300 hover:scale-105 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:rotate-90" />
              <span>Agregar</span>
            </div>
          </button>
          </div>

          {/* Lista de comentarios */}
          {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
            <div className="space-y-4">
              {selectedTicket.comments.map((comment: any, index: number) => (
                <div key={index} className={`p-4 ${bgClasses} rounded`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${textClasses}`}>{comment.author}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        comment.author_role === 'admin' ? 'bg-red-100 text-red-700' :
                        comment.author_role === 'technician' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {comment.author_role?.toUpperCase() || 'USER'}
                      </span>
                    </div>
                    <span className={`text-xs ${subTextClasses}`}>
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className={`text-sm ${textClasses}`}>{comment.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <MessageCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className={`${subTextClasses} text-sm`}>No hay comentarios aún</p>
            </div>
          )}

          {/* Formulario de comentarios */}
          {showCommentForm && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Escribir comentario..."
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
              
            
                <div className="flex justify-end space-x-3 mt-4">
                    {/* Botón Cancelar */}
                    <button
                      onClick={() => {
                        setShowCommentForm(false);
                        setCommentText('');
                        setCommentAttachments([]);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 font-medium text-sm"
                    >
                      Cancelar
                    </button>
                {/* ✅ Botón Enviar MODERNO */}
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim()}
                  className="group relative px-5 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-sm shadow-md hover:shadow-green-500/25 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center">
                    <Send className="w-4 h-4 mr-2" />
                    <span>Enviar</span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===============================================
  // 🎨 RENDERIZADO PRINCIPAL
  // ===============================================

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Notificaciones */}
      {renderNotification()}

      {/* Error */}
      {renderError()}

      {/* Header con estadísticas */}
      <div className="mb-6">
  <div className="flex items-center justify-between">
    <div>
      <h1 className={`text-2xl font-bold ${textClasses}`}>Sistema de Tickets</h1>
      <p className={`${subTextClasses} text-sm`}>
        Gestión de soporte técnico especializado
      </p>
    </div>
    
    <div className="flex items-center space-x-3">
      {/* ✅ Botón Actualizar MODERNO */}
      <button
        onClick={() => {
          loadTickets();
          updateHeaderStats();
        }}
        disabled={loading}
        className="group relative px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:scale-100 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative flex items-center">
          <RefreshCw className={`w-4 h-4 mr-2 transition-transform duration-300 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`} />
          <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
        </div>
      </button>
      
      {/* ✅ Botón Crear Ticket MODERNO */}
      <button
        onClick={handleCreateTicket}
        className="group relative px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative flex items-center">
          <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:rotate-90" />
          <span>Crear Ticket</span>
        </div>
      </button>
    </div>
  </div>
</div>

      {/* Filtros */}
      <div className={`${cardClasses} p-4 mb-6`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">Todos los estados</option>
            <option value="open">Abiertos</option>
            <option value="in_progress">En Progreso</option>
            <option value="resolved">Resueltos</option>
            <option value="closed">Cerrados</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">Todas las prioridades</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="critical">Crítica</option>
          </select>

          <div className="flex items-center justify-center">
            <span className={`${subTextClasses} text-sm`}>
              {filteredTickets.length} de {tickets.length} tickets
            </span>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Lista de tickets */}
        <div className="lg:col-span-2">
          <div className={`${cardClasses} p-4`}>
            <h2 className={`text-lg font-medium ${textClasses} mb-4`}>
              Tickets Recientes
            </h2>
            {renderTicketsList()}
          </div>
        </div>

        {/* Detalles del ticket */}
        <div className="lg:col-span-3">
          {renderTicketDetail()}
        </div>
      </div>

      {/* Modal CreateTicketForm */}
      <CreateTicketForm 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTicketCreated={handleTicketCreated}
        initialData={{
          user_name: currentUser?.name || '',
          user_email: currentUser?.email || ''
        }}
      />
    </div>
  );
};

export default TicketSystem;